import { useState, useEffect, useRef } from "react";
import { ticketsApi } from "../../utils/api";

export default function WristbandScanner({ events, activeEvent, setActiveEvent }) {
  const [ticketInput, setTicketInput]   = useState("");
  const [wristbandInput, setWristbandInput] = useState("");
  const [scanResult, setScanResult]     = useState(null);
  const [step, setStep]                 = useState("scan_ticket"); // scan_ticket | enter_wristband | done
  const [stats, setStats]               = useState(null);
  const [recent, setRecent]             = useState([]);
  const [loading, setLoading]           = useState(false);
  const ticketRef = useRef(null);
  const wristbandRef = useRef(null);

  useEffect(() => {
    if (activeEvent) loadStats();
  }, [activeEvent]);

  useEffect(() => {
    if (step === "scan_ticket") ticketRef.current?.focus();
    if (step === "enter_wristband") wristbandRef.current?.focus();
  }, [step]);

  const loadStats = async () => {
    try {
      const d = await ticketsApi.request('GET', `/api/tickets/manage/wristband/stats/${activeEvent.id}`);
      setStats(d.stats);
      setRecent(d.recent || []);
    } catch(e) {}
  };

  const handleTicketScan = async () => {
    if (!ticketInput.trim()) return;
    setLoading(true);
    try {
      // Verify ticket exists via checkin endpoint (read-only lookup)
      const uid = ticketInput.trim();
      // Just move to wristband step — will validate on assign
      setScanResult({ ticket_uid: uid, status: "ready" });
      setStep("enter_wristband");
      setTicketInput("");
    } catch(e) {
      setScanResult({ error: e.message });
    }
    setLoading(false);
  };

  const handleAssign = async () => {
    if (!wristbandInput.trim() || !scanResult?.ticket_uid) return;
    setLoading(true);
    try {
      const d = await ticketsApi.request('POST', '/api/tickets/manage/wristband/assign', {
        ticket_uid: scanResult.ticket_uid,
        wristband_number: wristbandInput.trim(),
        event_id: activeEvent?.id,
      });

      if (d.success) {
        setScanResult({ ...d, success: true });
        setStep("done");
        setWristbandInput("");
        loadStats();
      } else {
        setScanResult({ ...d, error: d.error });
        if (d.already) setStep("done");
      }
    } catch(e) {
      setScanResult({ error: e.message });
    }
    setLoading(false);
  };

  const reset = () => {
    setScanResult(null);
    setStep("scan_ticket");
    setTicketInput("");
    setWristbandInput("");
    setTimeout(() => ticketRef.current?.focus(), 100);
  };

  const inpStyle = { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)" };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-white">🎀 Wristband Scanner</h1>
        {events?.length > 1 && (
          <select value={activeEvent?.id||""} onChange={e => setActiveEvent(events.find(ev=>ev.id===parseInt(e.target.value)))}
            className="text-sm text-white px-3 py-2 rounded-xl focus:outline-none"
            style={inpStyle}>
            {events.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">

        {/* ── SCANNER PANEL ── */}
        <div className="space-y-4">

          {/* Stats bar */}
          {stats && (
            <div className="glass rounded-2xl p-4 grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl font-black text-white">{stats.total_tickets}</p>
                <p className="text-xs text-gray-500">Total Tickets</p>
              </div>
              <div>
                <p className="text-2xl font-black text-green-400">{stats.assigned}</p>
                <p className="text-xs text-gray-500">Wristbands Given</p>
              </div>
              <div>
                <p className="text-2xl font-black text-yellow-400">{stats.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          )}

          {/* Step indicator */}
          <div className="flex items-center gap-3 glass rounded-2xl p-4">
            {["scan_ticket","enter_wristband","done"].map((s,i) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  step === s ? "text-white" :
                  (i < ["scan_ticket","enter_wristband","done"].indexOf(step)) ? "bg-green-500 text-white" :
                  "bg-white/10 text-gray-500"
                }`} style={step===s?{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}:{}}>
                  {i < ["scan_ticket","enter_wristband","done"].indexOf(step) ? "✓" : i+1}
                </div>
                <p className={`text-xs font-medium ${step===s?"text-white":"text-gray-600"}`}>
                  {["Scan Ticket","Enter Wristband","Complete"][i]}
                </p>
                {i < 2 && <div className="flex-1 h-px bg-white/10"/>}
              </div>
            ))}
          </div>

          {/* Step 1: Scan Ticket */}
          {step === "scan_ticket" && (
            <div className="glass rounded-2xl p-6">
              <h2 className="text-white font-bold text-lg mb-1">Step 1: Scan Ticket QR</h2>
              <p className="text-gray-500 text-sm mb-4">Scan or enter the ticket UID from the attendee's QR code</p>
              <div className="flex gap-2">
                <input ref={ticketRef} type="text"
                  placeholder="Scan ticket QR or enter UID..."
                  value={ticketInput} onChange={e => setTicketInput(e.target.value)}
                  onKeyDown={e => e.key==="Enter" && handleTicketScan()}
                  className="flex-1 px-4 py-3 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  style={inpStyle}/>
                <button onClick={handleTicketScan} disabled={loading || !ticketInput.trim()}
                  className="px-5 py-3 rounded-xl text-white font-bold text-sm transition disabled:opacity-50"
                  style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                  {loading ? "..." : "Next →"}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Enter Wristband */}
          {step === "enter_wristband" && (
            <div className="glass rounded-2xl p-6" style={{border:"1px solid rgba(155,63,232,0.4)"}}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-green-400">✅</span>
                <h2 className="text-white font-bold text-lg">Step 2: Assign Wristband</h2>
              </div>
              <p className="text-gray-500 text-sm mb-1">Ticket scanned: <span className="text-brand-300 font-mono text-xs">{scanResult?.ticket_uid?.slice(0,16)}...</span></p>
              <p className="text-gray-500 text-sm mb-4">Enter the wristband number to assign</p>
              <div className="flex gap-2">
                <input ref={wristbandRef} type="text"
                  placeholder="e.g. 1279"
                  value={wristbandInput} onChange={e => setWristbandInput(e.target.value)}
                  onKeyDown={e => e.key==="Enter" && handleAssign()}
                  className="flex-1 px-4 py-3 rounded-xl text-white text-lg font-mono placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 text-center tracking-widest"
                  style={inpStyle}/>
                <button onClick={handleAssign} disabled={loading || !wristbandInput.trim()}
                  className="px-5 py-3 rounded-xl text-white font-bold text-sm transition disabled:opacity-50"
                  style={{background:"linear-gradient(135deg,#166534,#16a34a)"}}>
                  {loading ? "..." : "Assign ✓"}
                </button>
              </div>
              <button onClick={reset} className="mt-3 text-xs text-gray-600 hover:text-gray-400 transition">← Back</button>
            </div>
          )}

          {/* Step 3: Done */}
          {step === "done" && (
            <div className={`rounded-2xl p-6 ${scanResult?.success ? "border-green-500/50" : "border-red-500/50"}`}
              style={{background: scanResult?.success ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border:`2px solid ${scanResult?.success?"rgba(34,197,94,0.4)":"rgba(239,68,68,0.4)"}`}}>
              <div className="text-center mb-4">
                <p className="text-5xl mb-2">{scanResult?.success ? "🎀" : "❌"}</p>
                <p className={`font-black text-xl ${scanResult?.success?"text-green-300":"text-red-300"}`}>
                  {scanResult?.success ? "Wristband Assigned!" : scanResult?.already ? "Already Assigned!" : "Error!"}
                </p>
              </div>
              {scanResult?.success && (
                <div className="space-y-2 mb-4">
                  {[
                    ["👤 Attendee", scanResult.attendee_name],
                    ["🎫 Tier",     scanResult.tier_name],
                    ["🎀 Wristband", scanResult.wristband_number],
                  ].map(([l,v]) => (
                    <div key={l} className="flex justify-between text-sm">
                      <span className="text-gray-400">{l}</span>
                      <span className="text-white font-bold">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              {scanResult?.error && !scanResult?.success && (
                <p className="text-red-300 text-sm text-center mb-4">{scanResult.error}</p>
              )}
              <button onClick={reset}
                className="w-full py-3 rounded-xl text-white font-bold transition"
                style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                Scan Next Ticket →
              </button>
            </div>
          )}
        </div>

        {/* ── RECENT ASSIGNMENTS ── */}
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white">Recent Assignments</h2>
            <button onClick={loadStats} className="text-xs text-gray-500 hover:text-white transition">🔄 Refresh</button>
          </div>
          {stats && (
            <div className="mb-4 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: stats.total_tickets > 0 ? `${Math.round(stats.assigned/stats.total_tickets*100)}%` : "0%",
                background:"linear-gradient(90deg,#7c22c8,#16a34a)"
              }}/>
            </div>
          )}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {recent.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl"
                style={{background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.1)"}}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm text-white flex-shrink-0"
                  style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                  {r.wristband_number}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{r.attendee_name}</p>
                  <p className="text-xs text-gray-500">{r.tier_name}</p>
                </div>
                <span className="text-xs text-green-400 flex-shrink-0">
                  {r.wristband_assigned_at ? new Date(r.wristband_assigned_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) : ''}
                </span>
              </div>
            ))}
            {!recent.length && (
              <div className="text-center py-10">
                <p className="text-3xl mb-2">🎀</p>
                <p className="text-gray-500 text-sm">No wristbands assigned yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
